import { memo } from 'react';

import { Box, Divider, Icon, Text, TextButton } from '@zillow/constellation-10';
import type {
  ExistingFeesDto,
  FeeCategoryDictionaryItemDto,
  GetSingleFamilyFeesDictionaryItemsDto,
  OtherFeesPseudoCategoryDto,
  SingleFamilyFeeDto,
} from '@zg-rentals/rental-manager-env';

import { getIconByName } from '../../../../../shared/utils/iconUtils';
import { TRIGGER_LOCATIONS_MAP } from '../constants';

import CostsAndFeesSFCSEvents from '../../../../model/ClickstreamEvent/CostsAndFeesSFCSEvents';

import { FeeListItem } from './FeeListItem';

type Props = {
  dictionaryItems: GetSingleFamilyFeesDictionaryItemsDto;
  existingFees: ExistingFeesDto;
  handleAddFeeClick: (fixedCategory?: string) => void;
  handleEditFeeClick: (fee: SingleFamilyFeeDto) => void;
  displayedLocation: string;
};

// TODO: adjust the width of the display to match figma
export const FeesByCategory = memo(
  ({ dictionaryItems, existingFees, handleAddFeeClick, handleEditFeeClick, displayedLocation }: Props) => {
    const getFeeMainCategoryDisplay = (
      feeCategoryKey: string,
      dictionaryItems: Record<string, FeeCategoryDictionaryItemDto>,
    ) => {
      const { displayName, feeCategoryIconName } = dictionaryItems[feeCategoryKey];
      const IconComponent = getIconByName({ name: feeCategoryIconName, useFallbackIcon: true });
      return (
        <Box css={{ paddingBlock: '400', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
            {IconComponent && <Icon size="md" render={<IconComponent />} />}
            <Text textStyle="body-bold">{displayName}</Text>
          </Box>
          <TextButton
            onClick={() => handleAddFeeClick(feeCategoryKey)}
            data-testid={`CostsAndFees-Add${displayName}FeeButton`}
            data-eventid={CostsAndFeesSFCSEvents.ClickAddFee.data_event_id}
          >
            Add
          </TextButton>
        </Box>
      );
    };

    const getFeeOtherCategoryDisplay = (dictionaryItems: OtherFeesPseudoCategoryDto) => {
      const { displayName, feeCategoryIconName } = dictionaryItems;
      const IconComponent = getIconByName({ name: feeCategoryIconName, useFallbackIcon: true });
      return (
        <Box css={{ paddingBlock: '400', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
            {IconComponent && <Icon size="md" render={<IconComponent />} />}
            <Text textStyle="body-bold">{displayName}</Text>
          </Box>
          <TextButton
            onClick={() => handleAddFeeClick()}
            data-eventid={CostsAndFeesSFCSEvents.ClickAddFee.data_event_id}
          >
            Add
          </TextButton>
        </Box>
      );
    };

    return (
      <div data-testid="SingleFamilyFeesByCategory">
        {Object.keys(dictionaryItems.mainCategories).map((categoryKey) => (
          <Box key={categoryKey}>
            {getFeeMainCategoryDisplay(categoryKey, dictionaryItems.mainCategories)}

            <Divider />
            {existingFees.mainCategories[categoryKey as keyof typeof existingFees.mainCategories]?.map(
              (fee: SingleFamilyFeeDto) => (
                <Box
                  key={fee.costAndFeeId}
                  css={{
                    marginLeft: displayedLocation === TRIGGER_LOCATIONS_MAP.posting_path_review ? '800' : '0',
                  }}
                >
                  <FeeListItem
                    fee={fee}
                    dictionaryItems={dictionaryItems}
                    handleEditFeeClick={handleEditFeeClick}
                    displayedLocation={displayedLocation}
                  />
                </Box>
              ),
            )}
          </Box>
        ))}
        <Box key={'otherCategories'}>
          {getFeeOtherCategoryDisplay(dictionaryItems.otherCategories)}

          <Divider />
          {existingFees.otherCategories.map((fee: SingleFamilyFeeDto) => (
            <Box
              key={fee.costAndFeeId}
              css={{
                marginLeft: displayedLocation === TRIGGER_LOCATIONS_MAP.posting_path_review ? '800' : '0',
              }}
            >
              <FeeListItem
                fee={fee}
                dictionaryItems={dictionaryItems}
                handleEditFeeClick={handleEditFeeClick}
                displayedLocation={displayedLocation}
              />
            </Box>
          ))}
        </Box>
      </div>
    );
  },
);

FeesByCategory.displayName = 'FeesByCategory';
